/**
 * useFeature — the single React entry point for feature gating (B2B Phase A,
 * #1645/#1646/#1647). Derives the experience PRESET, composes overrides, and
 * returns {enabled, reason}. The precedence lives in the pure resolver.
 *
 * Usage:
 *   const { enabled } = useFeature('strategy');
 *   <FeatureGate feature="redline"><RedlineNav /></FeatureGate>
 */

import { useMemo } from 'react';
import posthog from 'posthog-js';
import { useAdvisorSafe } from '@/contexts/AdvisorContext';
import { useFirmManagedCase } from '@/hooks/useFirmManagedCase';
import { useFirmFeatureOverrides } from '@/hooks/useFirmFeatureOverrides';
import { useSubscription } from '@/hooks/useSubscription';
import { FEATURES, FEATURE_LIST, featureForRoute, type FeatureKey } from '@/lib/features/registry';
import { resolveFeature, type FeatureResolution } from '@/lib/features/resolve';
import type { FeaturePreset } from '@/lib/features/registry';

// Generic env overrides: VITE_FF_FEATURE_<UPPER_KEY>=true|false. Vite inlines all
// VITE_-prefixed vars into import.meta.env at build, so we can discover them.
const ENV_FEATURE_OVERRIDES: Record<string, boolean> = (() => {
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(import.meta.env)) {
    const m = /^VITE_FF_FEATURE_(.+)$/.exec(k);
    if (m && (v === 'true' || v === 'false')) out[m[1].toLowerCase()] = v === 'true';
  }
  return out;
})();

const envOverride = (key: string): boolean | undefined =>
  key in ENV_FEATURE_OVERRIDES ? ENV_FEATURE_OVERRIDES[key] : undefined;

const posthogOpinion = (flag: string): boolean | undefined =>
  typeof window !== 'undefined' && posthog.__loaded ? posthog.isFeatureEnabled(flag) : undefined;

// The literal "one switch": VITE_EXPERIENCE_PRESET=firm|individual|collector forces the
// whole product into one experience regardless of per-user firm-managed status
// (B2B Phase B, #1649). Default (unset) keeps per-user auto-detection.
const FORCED_PRESET: FeaturePreset | undefined = (() => {
  const v = import.meta.env.VITE_EXPERIENCE_PRESET;
  return v === 'firm' || v === 'individual' || v === 'collector' ? v : undefined;
})();

/**
 * The experience preset. A build/config switch (VITE_EXPERIENCE_PRESET) wins;
 * otherwise advisor mode or a firm-managed client → the firm-first experience
 * (individual features off by default); otherwise individual (legacy B2C).
 * One switch flips the whole product (#1647/#1649).
 */
export function useExperiencePreset(): FeaturePreset {
  const { isAdvisorMode } = useAdvisorSafe();
  const { isFirmManaged } = useFirmManagedCase();
  if (FORCED_PRESET) return FORCED_PRESET;
  return isAdvisorMode || isFirmManaged ? 'firm' : 'individual';
}

const overrideLookup = (overrides: Record<string, boolean>) => (key: string): boolean | undefined =>
  key in overrides ? overrides[key] : undefined;

export function useFeature(key: FeatureKey): FeatureResolution {
  const preset = useExperiencePreset();
  const { canAccessTier } = useSubscription();
  const firmOverrides = useFirmFeatureOverrides();
  const def = FEATURES[key];

  return useMemo(() => {
    if (!def) return { enabled: false, reason: 'unregistered' };
    return resolveFeature(def, {
      preset,
      posthog: posthogOpinion,
      env: envOverride,
      hasTier: canAccessTier,
      firmOverride: overrideLookup(firmOverrides),
    });
    // canAccessTier identity is stable enough; preset/def/overrides drive recompute.
  }, [def, preset, canAccessTier, firmOverrides]);
}

/** Convenience boolean. */
export function useFeatureEnabled(key: FeatureKey): boolean {
  return useFeature(key).enabled;
}

/**
 * Resolve the WHOLE registry once (for nav/route filtering, which can't call a
 * hook per item). Returns a { featureKey: enabled } map plus a route helper.
 */
export function useResolvedFeatures(): {
  byKey: Record<string, boolean>;
  routeEnabled: (pathname: string) => boolean;
} {
  const preset = useExperiencePreset();
  const { canAccessTier } = useSubscription();
  const firmOverrides = useFirmFeatureOverrides();

  return useMemo(() => {
    const ctx = {
      preset,
      posthog: posthogOpinion,
      env: envOverride,
      hasTier: canAccessTier,
      firmOverride: overrideLookup(firmOverrides),
    };
    const byKey: Record<string, boolean> = {};
    for (const f of FEATURE_LIST) byKey[f.key] = resolveFeature(f, ctx).enabled;
    // A route with no registered feature is not gated here (default visible).
    const routeEnabled = (pathname: string): boolean => {
      const f = featureForRoute(pathname);
      return f ? byKey[f.key] : true;
    };
    return { byKey, routeEnabled };
  }, [preset, canAccessTier, firmOverrides]);
}

export function FeatureGate({
  feature,
  children,
  fallback = null,
}: {
  feature: FeatureKey;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}): React.ReactElement {
  const { enabled } = useFeature(feature);
  return <>{enabled ? children : fallback}</>;
}
